import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Container, Typography, TextField, Button,
  Alert, Card, CardActionArea, CardContent, CircularProgress,
  Grid, Chip, Dialog, DialogContent, IconButton, useMediaQuery, Divider,
} from '@mui/material';
import { keyframes } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import PercentIcon from '@mui/icons-material/Percent';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import apiService from '../services/apiService';
import PricingSection from '../components/Reszletek/PricingSection';

const schema = yup.object().shape({
  businessName: yup.string().trim().required('A vállalkozás neve kötelező').max(100, 'Maximum 100 karakter'),
  firstName: yup.string().trim().required('A keresztnév megadása kötelező').max(50, 'Maximum 50 karakter'),
  lastName: yup.string().trim().required('A vezetéknév megadása kötelező').max(50, 'Maximum 50 karakter'),
  email: yup.string().email('Érvényes email cím szükséges').required('Az email cím kötelező'),
  phone: yup.string().optional().test('phone-format', 'Érvényes magyar mobiltelefonszám szükséges (pl. +36201234567)', (value) => {
    if (!value) return true;
    return /^\+36(20|30|70)\d{7}$/.test(value);
  }),
  city: yup.string().optional().max(100, 'Maximum 100 karakter'),
});

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Nézetváltás: az árazás jobbról érkezik, a részletek balról
const slideFromRight = keyframes`
  from { opacity: 0; transform: translateX(48px); }
  to { opacity: 1; transform: translateX(0); }
`;
const slideFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-48px); }
  to { opacity: 1; transform: translateX(0); }
`;

const VIEWS = [
  { key: 'reszletek', label: 'Részletek' },
  { key: 'arazas', label: 'Árazás' },
];

const drift = keyframes`
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-16px, 12px); }
`;

const highlights = [
  { icon: <CheckCircleOutlineIcon />, title: 'Ingyenes csatlakozás', text: 'A regisztráció és a platform használata teljesen díjmentes.' },
  { icon: <StarOutlineIcon />, title: 'Korai csatlakozóknak extra jutalom', text: 'Az elsők között csatlakozó szolgáltatóknak ingyenes kiemelést biztosítunk, hogy könnyebb legyen kitűnni a tömegből.' },
  { icon: <PercentIcon />, title: 'Csak sikeres foglalás után', text: 'A jutalék kategóriától függően 3–5%, és kizárólag teljesült foglalás esetén esedékes. A pontos mértéket az Árazás fülön találod.' },
  { icon: <MarkEmailReadOutlinedIcon />, title: 'Azonnali email értesítés', text: 'A platformon kapott megkeresésekről automatikusan emailt küldünk, így nem maradsz le semmiről.' },
  { icon: <TuneOutlinedIcon />, title: 'Rugalmasan szerkeszthető profil', text: 'Bármikor módosíthatod profilod adatait, képeit és beállításait.' },
  { icon: <StorefrontOutlinedIcon />, title: 'Dinamikusan növekvő piactér', text: 'Egy folyamatosan bővülő marketplace-en kapsz helyet, mely extra megtekintéseket hoz neked.' },
  { icon: <InsightsOutlinedIcon />, title: 'Valós statisztikák', text: 'Pontos adatokat kapsz arról, hányan és hogyan tekintik meg a személyes profilod.' },
  { icon: <FactCheckOutlinedIcon />, title: 'Egyedi ajánlatkérő kérdőív', text: 'Saját kérdőívet állíthatsz össze, amivel az ügyféltől előre bekérheted az árazáshoz szükséges összes részletet.' },
];

const Reszletek = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:599px)');
  // Aktív nézet: a #arazas hash közvetlenül az árazást nyitja (pl. a meghívó levél linkje)
  const [view, setView] = useState(location.hash === '#arazas' ? 'arazas' : 'reszletek');
  const tabsRef = useRef(null);

  useEffect(() => {
    setView(location.hash === '#arazas' ? 'arazas' : 'reszletek');
  }, [location.hash]);

  const switchView = (next) => {
    if (next === view) return;
    setView(next);
    // A hash-t frissítjük (megosztható link), de navigáció/görgetés nélkül
    window.history.replaceState(null, '', `${location.pathname}${location.search}${next === 'arazas' ? '#arazas' : ''}`);
    const top = tabsRef.current?.getBoundingClientRect().top;
    if (top !== undefined && top < 0) {
      tabsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const { handleSubmit, control, reset, formState: { errors, isSubmitting } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { businessName: '', firstName: '', lastName: '', email: '', phone: '', city: '' },
  });

  const openDialog = () => {
    setSubmitError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setTimeout(() => {
      setSubmitted(false);
      reset();
    }, 300);
  };

  const onSubmit = async (data) => {
    setSubmitError('');
    try {
      const payload = {
        businessName: data.businessName,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone || undefined,
        city: data.city || undefined,
      };
      const res = await apiService.post('/provider-signup', payload);
      if (res.success) setSubmitted(true);
      else setSubmitError(res.message || 'Hiba történt az űrlap küldése során.');
    } catch (err) {
      setSubmitError(err?.message || 'Hiba történt az űrlap küldése során. Kérjük, próbálja újra.');
    }
  };

  const actions = [
    {
      key: 'register',
      icon: <HowToRegOutlinedIcon fontSize="medium" />,
      title: 'Regisztráció',
      text: 'Te építed fel a profilod, pontosan úgy, ahogy megálmodtad.',
      onClick: () => navigate('/register'),
      chip: 'Teljes kontroll',
      highlight: true,
    },
    {
      key: 'signup',
      icon: <AutoAwesomeOutlinedIcon fontSize="medium" />,
      title: 'Feliratkozás',
      text: 'Add meg elérhetőségeid, és mi elkészítjük a profilod.',
      onClick: openDialog,
      chip: 'Kényelmes',
      highlight: true,
    },
    {
      key: 'login',
      icon: <LoginOutlinedIcon fontSize="medium" />,
      title: 'Bejelentkezés',
      text: 'Már elkészítettük a profilod.',
      onClick: () => navigate('/login'),
      chip: null,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #f7f0e0 0%, #f0ede6 45%, #eef1e9 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(rgba(42,93,50,0.08) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: -120,
          right: -120,
          width: 420,
          height: 420,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(237,190,95,0.35) 0%, rgba(237,190,95,0) 70%)',
          animation: `${drift} 14s ease-in-out infinite`,
          pointerEvents: 'none',
        },
      }}
    >
      <Helmet>
        <title>Partnerség és árazás szolgáltatóknak | Wedigo</title>
        <meta name="description" content="Esküvői szolgáltató vagy? Csatlakozz a Wedigóhoz: ingyenes profil, foglalások és üzenetek egy helyen, kiemelési csomagok és átlátható árazás." />
        <meta property="og:title" content="Partnerség és árazás szolgáltatóknak | Wedigo" />
        <meta property="og:description" content="Esküvői szolgáltató vagy? Csatlakozz a Wedigóhoz: ingyenes profil, foglalások és üzenetek egy helyen, kiemelési csomagok és átlátható árazás." />
        <meta property="og:url" content="https://wedigo.hu/reszletek" />
        <link rel="canonical" href="https://wedigo.hu/reszletek" />
      </Helmet>
      <Container maxWidth="md" sx={{ position: 'relative', py: { xs: 6, sm: 9 }, px: { xs: 2.5, sm: 3 } }}>

        {/* ── Hero ───────────────────────────────────────────── */}
        <Box sx={{ textAlign: 'center', mb: { xs: 5, sm: 7 }, animation: `${fadeUp} 0.7s ease both` }}>
          <Typography
            variant="overline"
            sx={{ color: '#2a5d32', letterSpacing: '0.25em', fontWeight: 700 }}
          >
            Wedigo Partnerprogram
          </Typography>
          <Typography
            variant="h2"
            sx={{
              mt: 1,
              fontSize: { xs: '2.1rem', sm: '2.9rem' },
              color: '#1b331f',
              lineHeight: 1.15,
            }}
          >
            Csatlakozz a Wedigo<br />esküvői piactérhez
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, my: 2.5 }}>
            <Box sx={{ width: { xs: 32, sm: 56 }, height: '1px', bgcolor: 'rgba(237,190,95,0.9)' }} />
            <Box sx={{ width: 8, height: 8, bgcolor: '#edbe5f', transform: 'rotate(45deg)' }} />
            <Box sx={{ width: { xs: 32, sm: 56 }, height: '1px', bgcolor: 'rgba(237,190,95,0.9)' }} />
          </Box>

          <Typography variant="body1" sx={{ color: '#5b6e5d', maxWidth: 480, mx: 'auto', fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
            Minden, amit a partnerségről tudni érdemes.
          </Typography>
        </Box>

        {/* ── Nézetváltó: Részletek | Árazás ──────────────────── */}
        <Box
          ref={tabsRef}
          sx={{
            position: 'sticky',
            top: { xs: 8, sm: 16 },
            zIndex: 5,
            display: 'flex',
            justifyContent: 'center',
            mb: { xs: 3, sm: 4 },
            scrollMarginTop: 8,
            animation: `${fadeUp} 0.7s ease both`,
            animationDelay: '0.15s',
          }}
        >
          <Box
            role="tablist"
            sx={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              width: { xs: '100%', sm: 360 },
              p: 0.5,
              borderRadius: 999,
              bgcolor: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(6px)',
              border: '1px solid rgba(42,93,50,0.15)',
              boxShadow: '0 6px 20px rgba(27,51,31,0.10)',
            }}
          >
            {/* Csúszó kiemelés az aktív fül alatt */}
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                bottom: 4,
                left: 4,
                width: 'calc(50% - 4px)',
                borderRadius: 999,
                bgcolor: '#1b331f',
                transform: view === 'arazas' ? 'translateX(100%)' : 'translateX(0)',
                transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
            {VIEWS.map(v => (
              <Box
                key={v.key}
                component="button"
                type="button"
                role="tab"
                aria-selected={view === v.key}
                onClick={() => switchView(v.key)}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  border: 0,
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  py: 1.25,
                  font: 'inherit',
                  fontSize: { xs: 15, sm: 16 },
                  fontWeight: 700,
                  color: view === v.key ? '#fff' : '#2a5d32',
                  transition: 'color 0.3s',
                }}
              >
                {v.label}
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Nézetek (az aktív jobbról / balról csúszik be) ───── */}
        <Box sx={{ overflowX: 'hidden', mx: { xs: -2.5, sm: 0 }, px: { xs: 2.5, sm: 0 } }}>
          {view === 'reszletek' ? (
            <Box key="reszletek" sx={{ animation: `${slideFromLeft} 0.45s cubic-bezier(0.4, 0, 0.2, 1) both` }}>
            <Card
              variant="outlined"
              sx={{
                mb: { xs: 6, sm: 8 },
                borderColor: 'rgba(42,93,50,0.15)',
                bgcolor: 'rgba(255,255,255,0.6)',
                backdropFilter: 'blur(2px)',
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, sm: 1 } }}>
                {highlights.map((h, i) => (
                  <React.Fragment key={h.title}>
                    {i > 0 && <Divider sx={{ borderColor: 'rgba(42,93,50,0.1)', mx: { sm: 2 } }} />}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2.5,
                        py: { xs: 1.75, sm: 2.25 },
                        px: { sm: 1.5 },
                      }}
                    >
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: 48, height: 48,
                          borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: 'rgba(42,93,50,0.08)',
                          border: '1px solid rgba(42,93,50,0.18)',
                          color: '#2a5d32',
                        }}
                      >
                        {h.icon}
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1b331f' }}>
                          {h.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#5b6e5d' }}>
                          {h.text}
                        </Typography>
                      </Box>
                    </Box>
                  </React.Fragment>
                ))}
              </CardContent>
            </Card>

            <Box sx={{ textAlign: 'center', mb: { xs: 5, sm: 7 }, animation: `${fadeUp} 0.7s ease both`, animationDelay: '0.3s' }}>
              <Typography variant="body2" sx={{ color: '#5b6e5d', mb: 1.5 }}>
                Kíváncsi vagy, kik vannak már fent? Nézz körül, mielőtt döntesz.
              </Typography>
              <Button
                component={Link}
                to="/szolgaltatok"
                variant="outlined"
                startIcon={<StorefrontOutlinedIcon />}
                sx={{
                  borderColor: '#2a5d32',
                  color: '#2a5d32',
                  fontWeight: 600,
                  '&:hover': { borderColor: '#1b331f', bgcolor: 'rgba(42,93,50,0.06)' },
                }}
              >
                Nézd meg a piacteret!
              </Button>
            </Box>

            </Box>
          ) : (
            <Box key="arazas" sx={{ animation: `${slideFromRight} 0.45s cubic-bezier(0.4, 0, 0.2, 1) both` }}>
              <PricingSection />
            </Box>
          )}
        </Box>

        {/* ── Actions ────────────────────────────────────────── */}
        <Typography
          variant="h6"
          align="center"
          sx={{ color: '#1b331f', mb: 3, animation: `${fadeUp} 0.7s ease both`, animationDelay: '0.35s' }}
        >
          Hogyan szeretnél csatlakozni?
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2, maxWidth: 640, mx: 'auto' }}>
          {actions.map((a, i) => (
            <Grid size={{ xs: 12, sm: 4 }} key={a.key}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  position: 'relative',
                  animation: `${fadeUp} 0.7s ease both`,
                  animationDelay: `${0.45 + i * 0.1}s`,
                  borderColor: a.highlight ? '#edbe5f' : 'rgba(42,93,50,0.15)',
                  borderWidth: a.highlight ? 2 : 1,
                  boxShadow: a.highlight ? '0 8px 24px rgba(237,190,95,0.28)' : 'none',
                  bgcolor: a.highlight ? '#fffaf0' : '#fff',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: a.highlight
                      ? '0 14px 32px rgba(237,190,95,0.38)'
                      : '0 10px 24px rgba(27,51,31,0.12)',
                  },
                }}
              >
                <CardActionArea onClick={a.onClick} sx={{ height: '100%', p: 0.25 }}>
                  <CardContent sx={{ textAlign: 'center', py: 2, px: 2, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ height: 24, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {a.chip && (
                        <Chip
                          label={a.chip}
                          size="small"
                          sx={{
                            bgcolor: '#edbe5f', color: '#1b331f', fontWeight: 700, fontSize: 11,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ height: 28, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: a.highlight ? '#c9932a' : '#2a5d32' }}>
                      {a.icon}
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ color: '#1b331f', mb: 0.5, fontSize: 14, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      {a.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: '#5b6e5d', height: 54, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
                    >
                      {a.text}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Typography variant="caption" align="center" display="block" sx={{ color: '#5b6e5d', mt: 4 }}>
          Kérdésed van? Írj nekünk a <a href="mailto:hello@wedigo.hu" style={{ color: '#2a5d32', fontWeight: 600 }}>hello@wedigo.hu</a> címen.
        </Typography>
      </Container>

      {/* ── Feliratkozás dialog ────────────────────────────── */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { bgcolor: '#fdfbf6' } }}
      >
        <IconButton
          onClick={closeDialog}
          sx={{ position: 'absolute', right: 12, top: 12, color: '#5b6e5d', zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
          {submitted ? (
            <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 3 } }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#2a5d32', mb: 2 }} />
              <Typography variant="h5" fontWeight={700} sx={{ color: '#1b331f', mb: 1 }}>
                Köszönjük a jelentkezést!
              </Typography>
              <Typography variant="body1" sx={{ color: '#5b6e5d', mb: 3 }}>
                Hamarosan felvesszük veled a kapcsolatot, és elkészítjük a profilod a megadott adatok alapján.
              </Typography>
              <Button variant="contained" onClick={closeDialog} sx={{ bgcolor: '#1b331f', '&:hover': { bgcolor: '#2a5d32' }, px: 4 }}>
                Bezárás
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ color: '#c9932a', mb: 1 }}>
                  <AutoAwesomeOutlinedIcon fontSize="large" />
                </Box>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#1b331f' }}>
                  Profil igénylése
                </Typography>
                <Typography variant="body2" sx={{ color: '#5b6e5d', mt: 0.5 }}>
                  Add meg elérhetőségeid — a többit intézzük.
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Controller name="firstName" control={control} render={({ field }) => (<TextField {...field} fullWidth label="Keresztnév" error={!!errors.firstName} helperText={errors.firstName?.message} disabled={isSubmitting} />)} />
                    <Controller name="lastName" control={control} render={({ field }) => (<TextField {...field} fullWidth label="Vezetéknév" error={!!errors.lastName} helperText={errors.lastName?.message} disabled={isSubmitting} />)} />
                  </Box>
                  <Controller name="businessName" control={control} render={({ field }) => (<TextField {...field} fullWidth label="Vállalkozás neve" error={!!errors.businessName} helperText={errors.businessName?.message} disabled={isSubmitting} />)} />
                  <Controller name="email" control={control} render={({ field }) => (<TextField {...field} fullWidth label="Email cím" type="email" error={!!errors.email} helperText={errors.email?.message} disabled={isSubmitting} />)} />
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Controller name="phone" control={control} render={({ field }) => (<TextField {...field} fullWidth label="Telefonszám" placeholder="+36201234567" error={!!errors.phone} helperText={errors.phone?.message} disabled={isSubmitting} />)} />
                    <Controller name="city" control={control} render={({ field }) => (<TextField {...field} fullWidth label="Város" error={!!errors.city} helperText={errors.city?.message} disabled={isSubmitting} />)} />
                  </Box>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                    sx={{ mt: 1, py: 1.5, fontWeight: 700, fontSize: '1rem', bgcolor: '#1b331f', '&:hover': { bgcolor: '#2a5d32' } }}
                  >
                    {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Profil igénylése'}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Reszletek;
